import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import { HttpHelpService } from '../service/http-help.service';

export interface MobileAppVersionConfig {
  id: number;
  latest_version: string;
  minimum_required_version: string;
  update_message_en: string | null;
  update_message_ar: string | null;
  update_message_he: string | null;
  google_play_url: string | null;
  app_store_url: string | null;
  is_active: boolean;
  created_by: number | null;
  updated_by: number | null;
  createdDate: string;
  updatedDate: string;
}

/** Validates that the version string matches X.Y.Z */
function semverValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    return /^\d+\.\d+\.\d+$/.test(control.value) ? null : { semver: true };
  };
}

/** Cross-field validator: minimum_required_version must be <= latest_version */
function minimumLeLatestValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const latest = (group.get('latest_version')?.value ?? '') as string;
    const minimum = (group.get('minimum_required_version')?.value ?? '') as string;
    if (!latest || !minimum) return null;
    if (!/^\d+\.\d+\.\d+$/.test(latest) || !/^\d+\.\d+\.\d+$/.test(minimum)) return null;
    return compareVersions(minimum, latest) <= 0 ? null : { minimumExceedsLatest: true };
  };
}

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] - pb[i];
  }
  return 0;
}

@Component({
  selector: 'app-admin-app-version',
  templateUrl: './admin-app-version.component.html',
  styleUrls: ['./admin-app-version.component.scss'],
})
export class AdminAppVersionComponent implements OnInit {
  curentLang = localStorage.getItem('curentLang') || 'ar';

  currentConfig: MobileAppVersionConfig | null = null;
  historyRows: any[] = [];
  showHistory = false;
  loadingHistory = false;
  activeTab: 'ar' | 'en' | 'he' = 'ar';

  form = new FormGroup(
    {
      latest_version: new FormControl('', [Validators.required, semverValidator()]),
      minimum_required_version: new FormControl('', [Validators.required, semverValidator()]),
      update_message_ar: new FormControl(''),
      update_message_en: new FormControl(''),
      update_message_he: new FormControl(''),
      google_play_url: new FormControl('', [Validators.maxLength(500)]),
      app_store_url: new FormControl('', [Validators.maxLength(500)]),
      is_active: new FormControl(true),
    },
    { validators: minimumLeLatestValidator() },
  );

  constructor(
    private service: HttpHelpService,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    this.loadCurrentConfig();
  }

  t(ar: string, en: string, he: string): string {
    if (this.curentLang === 'ar') return ar;
    if (this.curentLang === 'he') return he;
    return en;
  }

  loadCurrentConfig(): void {
    this.spinner.show();
    this.service.getAdminAppVersion().subscribe({
      next: (config: MobileAppVersionConfig | null) => {
        this.spinner.hide();
        this.currentConfig = config;
        if (config) {
          this.form.patchValue({
            latest_version: config.latest_version,
            minimum_required_version: config.minimum_required_version,
            update_message_ar: config.update_message_ar ?? '',
            update_message_en: config.update_message_en ?? '',
            update_message_he: config.update_message_he ?? '',
            google_play_url: config.google_play_url ?? '',
            app_store_url: config.app_store_url ?? '',
            is_active: config.is_active,
          });
        }
      },
      error: () => {
        this.spinner.hide();
      },
    });
  }

  invalid(field: string): boolean {
    const c = this.form.get(field);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  get crossFieldError(): boolean {
    return !!(
      this.form.errors?.['minimumExceedsLatest'] &&
      this.form.get('latest_version')?.touched &&
      this.form.get('minimum_required_version')?.touched
    );
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const v = this.form.getRawValue();
    const body = {
      latest_version: v.latest_version!.trim(),
      minimum_required_version: v.minimum_required_version!.trim(),
      update_message_ar: v.update_message_ar?.trim() || undefined,
      update_message_en: v.update_message_en?.trim() || undefined,
      update_message_he: v.update_message_he?.trim() || undefined,
      google_play_url: v.google_play_url?.trim() || undefined,
      app_store_url: v.app_store_url?.trim() || undefined,
      is_active: v.is_active ?? true,
    };

    this.spinner.show();
    this.service.upsertAdminAppVersion(body).subscribe({
      next: (res) => {
        this.spinner.hide();
        const msg =
          this.curentLang === 'ar'
            ? res?.messageAr
            : this.curentLang === 'he'
              ? res?.messageHe
              : res?.messageEn;
        Swal.fire({
          icon: 'success',
          title: this.t('تم الحفظ', 'Saved', 'נשמר'),
          text: msg || '',
          timer: 3000,
          showConfirmButton: false,
        });
        this.currentConfig = res?.data ?? this.currentConfig;
        if (res?.data) this.currentConfig = res.data;
      },
      error: (err) => {
        this.spinner.hide();
        const errors: string[] = err?.error?.message;
        if (Array.isArray(errors) && errors.length) {
          Swal.fire({
            icon: 'error',
            title: this.t('خطأ في التحقق', 'Validation error', 'שגיאת אימות'),
            html: `<ul class="text-start mb-0">${errors.map((e) => `<li>${e}</li>`).join('')}</ul>`,
          });
        } else {
          const msg =
            err?.error?.messageAr || err?.error?.messageEn || err?.error?.message || this.t('فشل الحفظ', 'Save failed', 'השמירה נכשלה');
          Swal.fire({ icon: 'error', title: msg });
        }
      },
    });
  }

  confirmDeactivate(): void {
    if (!this.currentConfig?.id) return;
    const id = this.currentConfig.id;
    Swal.fire({
      icon: 'warning',
      title: this.t('إلغاء التفعيل؟', 'Deactivate?', 'לבטל הפעלה?'),
      text: this.t(
        'سيتوقف التطبيق عن إرسال تنبيهات التحديث حتى تُضبط إعدادات جديدة.',
        'The app will stop sending update alerts until new settings are configured.',
        'האפליקציה תפסיק לשלוח התראות עדכון עד שיוגדרו הגדרות חדשות.',
      ),
      showCancelButton: true,
      confirmButtonText: this.t('إلغاء التفعيل', 'Deactivate', 'בטל הפעלה'),
      cancelButtonText: this.t('تراجع', 'Cancel', 'ביטול'),
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.spinner.show();
      this.service.deactivateAdminAppVersion(id).subscribe({
        next: (res) => {
          this.spinner.hide();
          const msg =
            this.curentLang === 'ar'
              ? res?.messageAr
              : this.curentLang === 'he'
                ? res?.messageHe
                : res?.messageEn;
          Swal.fire({
            icon: 'success',
            title: this.t('تم إلغاء التفعيل', 'Deactivated', 'ההפעלה בוטלה'),
            text: msg || '',
            timer: 3000,
            showConfirmButton: false,
          });
          this.currentConfig = null;
          this.form.reset({ is_active: true });
        },
        error: (err) => {
          this.spinner.hide();
          const msg = err?.error?.messageAr || err?.error?.messageEn || err?.error?.message || this.t('فشل الطلب', 'Request failed', 'הבקשה נכשלה');
          Swal.fire({ icon: 'error', title: msg });
        },
      });
    });
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory && this.historyRows.length === 0) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    this.loadingHistory = true;
    this.service.getAdminAppVersionHistory().subscribe({
      next: (rows) => {
        this.historyRows = rows ?? [];
        this.loadingHistory = false;
      },
      error: () => {
        this.loadingHistory = false;
      },
    });
  }

  impactLabel(userVersion: string): string {
    const latest = this.form.get('latest_version')?.value ?? '';
    const minimum = this.form.get('minimum_required_version')?.value ?? '';
    if (!latest || !minimum || !userVersion) return '—';
    if (!/^\d+\.\d+\.\d+$/.test(latest) || !/^\d+\.\d+\.\d+$/.test(minimum)) return '—';
    if (compareVersions(userVersion, minimum) < 0) return this.t('إجباري', 'Forced', 'כפוי');
    if (compareVersions(userVersion, latest) < 0) return this.t('اختياري', 'Optional', 'אופציונלי');
    return this.t('لا شيء', 'None', 'ללא');
  }

  impactClass(userVersion: string): string {
    const label = this.impactLabel(userVersion);
    if (label === this.t('إجباري', 'Forced', 'כפוי')) return 'badge bg-danger';
    if (label === this.t('اختياري', 'Optional', 'אופציונלי')) return 'badge bg-warning text-dark';
    return 'badge bg-success';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }

  decrementVersion(v: string): string {
    if (!v || !/^\d+\.\d+\.\d+$/.test(v)) return '—';
    const parts = v.split('.').map(Number);
    if (parts[2] > 0) { parts[2]--; }
    else if (parts[1] > 0) { parts[1]--; parts[2] = 9; }
    else if (parts[0] > 0) { parts[0]--; parts[1] = 9; parts[2] = 9; }
    else return '—';
    return parts.join('.');
  }

  midVersion(): string {
    const latest = this.form.get('latest_version')?.value ?? '';
    const minimum = this.form.get('minimum_required_version')?.value ?? '';
    if (!latest || !minimum || !/^\d+\.\d+\.\d+$/.test(latest) || !/^\d+\.\d+\.\d+$/.test(minimum)) return '—';
    if (compareVersions(minimum, latest) >= 0) return '—';
    const pa = minimum.split('.').map(Number);
    const pb = latest.split('.').map(Number);
    const mid = pa.map((v, i) => Math.floor((v + pb[i]) / 2));
    if (mid.join('.') === minimum || mid.join('.') === latest) {
      const patch = pa[2] + 1;
      return `${pa[0]}.${pa[1]}.${patch}`;
    }
    return mid.join('.');
  }
}
